import { apiPost } from './api';

interface UploadUrl {
  uploadUrl: string;
  publicUrl: string;
  key: string;
  expiresInSeconds: number;
}

/**
 * Upload a single image to S3-compatible storage (MinIO in dev, DigitalOcean
 * Spaces in prod) via a backend-issued presigned PUT URL. The backend only
 * vends the signed URL — the bytes go straight from the browser to the bucket.
 * Returns the public URL of the uploaded file.
 *
 * @param presignPath backend route that returns a presigned upload URL,
 *   e.g. `/uploads/products/<id>/images/upload-url`.
 */
async function uploadImage(presignPath: string, file: File): Promise<string> {
  // 1) ask backend for a presigned URL
  const presigned = await apiPost<UploadUrl>(presignPath, {
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
  });
  // 2) PUT the bytes directly to the bucket — backend never sees them.
  // The presigned URL signs `x-amz-acl: public-read` (so the object's URL loads
  // without signing on DigitalOcean Spaces, which has no bucket-policy support),
  // so we must send that exact header or the signature won't match.
  const res = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'x-amz-acl': 'public-read',
    },
    body: file,
  });
  if (!res.ok) {
    throw new Error(`Storage upload failed: ${res.status} ${res.statusText}`);
  }
  return presigned.publicUrl;
}

/** Upload an image for a single product variant. Returns its public URL. */
export function uploadProductImage(productId: string, file: File): Promise<string> {
  return uploadImage(`/uploads/products/${productId}/images/upload-url`, file);
}

/** Upload an image for a product model (shared across its size variants). */
export function uploadModelImage(modelId: string, file: File): Promise<string> {
  return uploadImage(`/uploads/product-models/${modelId}/images/upload-url`, file);
}
